import type { SxProps, Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";
import { alpha } from "@mui/material/styles";
import {
  HEADER_HEIGHT,
  SIDE_PADDING,
  PRIMARY_COLOR,
  TEXT_SECONDARY,
} from "../../theme/consts";

export const getHeaderSx = (isDarkMode: boolean): SxProps<Theme> => ({
  backgroundColor: isDarkMode ? alpha("#000000", 0.2) : alpha("#FFFFFF", 0.3),
  backdropFilter: "blur(10px)",
  color: isDarkMode ? "#FFFFFF" : "text.primary",
  zIndex: 5,
  height: HEADER_HEIGHT,
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
});

export const logoLinkSx: SxProps<Theme> = {
  textDecoration: "none",
  display: "flex",
  alignItems: "center",
  "&:hover": {
    opacity: 0.9,
  },
};

export const logoImageSx: SxProps<Theme> = {
  height: "auto",
  maxHeight: "40px",
  width: "166px",
  display: "block",
};

export const getMenuButtonSx = (isDarkMode: boolean): SxProps<Theme> => ({
  color: isDarkMode ? "#FFFFFF" : "text.primary",
});

export const getButtonSx = (isDarkMode: boolean): SxProps<Theme> => ({
  display: {
    xs: "none",
    sm: "block",
  },
  border: isDarkMode ? "1px solid #FFFFFF" : "1px solid #C4C4C4",
  borderRadius: "1rem",
  backgroundColor: isDarkMode ? "transparent" : "#f8fafc",
  color: isDarkMode ? "#FFFFFF" : TEXT_SECONDARY,
  height: "2.5rem",
  fontSize: "1.125rem",
  boxShadow: "none",
  padding: "0 3.5rem",
  transition: "all 0.3s ease",
  textDecoration: "none",
  "&:hover": {
    transition: "all 0.3s ease",
    backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.1)" : "#222222",
    color: isDarkMode ? "#FFFFFF" : "#F5F5F5",
    boxShadow: "none",
  },
});

export const getNavigationBarSx = (
  isMenuOpen: boolean,
  isDarkMode: boolean,
): SxProps<Theme> => ({
  position: "fixed",
  top: HEADER_HEIGHT,
  left: 0,
  right: 0,
  zIndex: 4,
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  justifyContent: "flex-start",
  background: isDarkMode ? "rgba(0, 0, 0, 0.7)" : "rgba(139, 139, 139, 0.26)",
  backdropFilter: "blur(10px)",
  opacity: isMenuOpen ? 1 : 0,
  transition: "opacity 0.3s ease-out",
  pointerEvents: isMenuOpen ? "auto" : "none",
});

export const desktopNavWrapperSx: SxProps<Theme> = {
  width: "100%",
  maxWidth: "1440px",
  margin: "0 auto",
  paddingX: SIDE_PADDING.md,
  height: "100%",
};

export const navLinkSx: SystemStyleObject<Theme> = {
  background: "none",
  border: "none",
  borderBottom: "2px solid transparent",
  cursor: "pointer",
  padding: "0.5rem 1.5rem",
  fontSize: "0.875rem",
  fontWeight: 500,
  color: TEXT_SECONDARY,
  transition: "all 0.2s ease",
  fontFamily: "inherit",
  height: "100%",
  display: "flex",
  alignItems: "center",
  "&:hover": {
    color: PRIMARY_COLOR,
  },
};

export const getNavLinkActiveSx = (
  isActive: boolean,
  isDarkMode: boolean,
): SystemStyleObject<Theme> => ({
  color: isActive ? PRIMARY_COLOR : isDarkMode ? "#FFFFFF" : "#FFFFFF",
  borderBottomColor: isActive ? PRIMARY_COLOR : "transparent",
});

export const getMobileNavOverlaySx = (isDarkMode: boolean): SxProps<Theme> => ({
  width: "100%",
  height: `calc(100svh - ${HEADER_HEIGHT})`,
  maxHeight: `calc(100svh - ${HEADER_HEIGHT})`,
  minHeight: "600px",
  overflowY: "auto",
  padding: "1.25rem",
  backgroundColor: isDarkMode
    ? "rgba(17, 17, 17, 0.95)"
    : "rgba(255, 255, 255, 0.75)",
});

export const mobileNavSectionButtonSx: SxProps<Theme> = {
  width: "100%",
  border: "none",
  background: "transparent",
  textAlign: "left",
  padding: 0,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: ".5rem",
  cursor: "pointer",
};

export const getMobileNavLabelSx = (
  isActive: boolean,
  isDarkMode: boolean,
): SxProps<Theme> => ({
  fontSize: "2.125rem",
  fontWeight: 500,
  lineHeight: 1.25,
  color: isActive
    ? PRIMARY_COLOR
    : isDarkMode
      ? "rgba(255, 255, 255, 0.92)"
      : "rgba(20, 20, 20, 0.92)",
});

export const mobileNavExcerptSx: SxProps<Theme> = {
  fontSize: "1rem",
  fontWeight: 300,
  lineHeight: 1.5,
  color: TEXT_SECONDARY,
};

export const getMobileNavIconSx = (
  isActive: boolean,
  isDarkMode: boolean,
): SxProps<Theme> => ({
  width: "1.5rem",
  height: "1.5rem",
  color: isActive
    ? PRIMARY_COLOR
    : isDarkMode
      ? "rgba(255, 255, 255, 0.7)"
      : TEXT_SECONDARY,
});

export const getMobileNavDividerSx = (isDarkMode: boolean): SxProps<Theme> => ({
  backgroundColor: isDarkMode
    ? "rgba(255, 255, 255, 0.2)"
    : `${TEXT_SECONDARY}66`,
  height: "1px",
  width: "100%",
});
